import * as React from "react"
import { Row } from "react-grid-system"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer } from "../atoms/spacers"
import H3 from "../atoms/typography/H3"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import { AddIcon } from "../atoms/icons/AddIcon"
import { PersonRemoveIcon } from "../atoms/icons/PersonRemoveIcon"
import MemberDropdownBar from "../molecules/MemberDropdownBar"
import { useRemoveUsersFromGroup } from "../../hooks/useRemoveUsersFromGroup"
import { useRemoveUserFromOrganization } from "../../hooks/useRemoveUserFromOrganization"
import H4 from "../atoms/typography/H4"
import { AnimatePresence } from "framer-motion"
import Modal from "../molecules/Modals/Modal"
import RemoveMemberFromOrgOrGroupModalContent from "../molecules/Modals/RemoveMemberFromOrgOrGroupModalContent"
import MemberActionBar from "../molecules/MemberActionBar"
import { Member, OrganizationGroupType } from "../../utils/models"
import { captureExceptionWithContext } from "../../utils/sentry"
import { ApolloError } from "@apollo/client"

interface Props {
  member: Member
  myOrgName: string
  canEdit?: boolean
  onClickInviteToGroup: () => void
  open: boolean
  onClickOpen: (id: string) => void
}

const ContentContainer = styled.div<{ open: boolean }>`
  background-color: ${(props) => props.theme.colors.greyLight};
  width: 100%;
  border-radius: ${(props) => props.theme.borders.softRadius};
`

const Content = styled.div`
  padding: 15px;
`

const NameRoleSection = styled.div`
  display: flex;
  align-items: center;
`

const OrgRow = styled(Row)`
  padding: ${(props) => props.theme.spacings.extreme};
  background-color: ${(props) => props.theme.colors.greyLighter};
  margin: ${(props) => props.theme.spacings.large};
`

const EditMemberSectionDropdown: React.FunctionComponent<Props> = ({
  member,
  myOrgName,
  canEdit,
  onClickInviteToGroup,
  open,
  onClickOpen,
}) => {
  const [removeFromOrgModalOpen, setremoveFromOrgModalOpen] = React.useState(false)

  const onCompletedRemoveFromGroup = () => {
    console.log("Removed from group")
  }
  const onErrorRemoveFromGroup = (error: ApolloError) => {
    captureExceptionWithContext(error, "error removing from group")
  }

  const { removeUsersFromGroup, loading } = useRemoveUsersFromGroup({
    onCompleted: onCompletedRemoveFromGroup,
    onError: onErrorRemoveFromGroup,
  })

  const onClickRemoveFromGroup = async (groupId: number | string, orgId: number | string) => {
    try {
      await removeUsersFromGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: orgId,
          users: [member.member.email],
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error removing from group")
    }
  }

  const onCompletedRemoveFromOrg = () => {
    console.log("Removed from org")
    setremoveFromOrgModalOpen(false)
  }
  const onErrorRemoveFromOrg = (error: ApolloError) => {
    captureExceptionWithContext(error, "error removing from org")
  }

  const { removeUserFromOrganization } = useRemoveUserFromOrganization({
    onCompleted: onCompletedRemoveFromOrg,
    onError: onErrorRemoveFromOrg,
  })

  const onClickRemoveFromOrganization = async () => {
    try {
      await removeUserFromOrganization({
        variables: {
          email: member.member.email,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error removing from org")
    }
  }

  function findRoleInGroup(group: OrganizationGroupType, member: Member): string | undefined {
    // Find the role of the member in the group. A user can only be in one role in a group.
    if (group.roles) {
      for (const role of group.roles) {
        if (role.users?.some((user) => user.id === member.member.id)) {
          return role.name
        }
      }
    }

    return undefined // Member not found in any role -> something is wrong
  }

  return (
    <ContentContainer open={open}>
      <div style={{ padding: "0 15px" }}>
        <MemberDropdownBar
          imgSrc={member.member?.src ?? ""}
          name={member.member.name}
          title={member.member.title}
          role={member.member.role}
          suborg={member.member.suborg}
          myOrgName={myOrgName}
          open={open}
          onClick={() => onClickOpen(member.member.id)}
          email={member.member.email}
        />
      </div>
      <SlideOutHeight isVisible={open}>
        <HorizontalSpacer spacing={1} />
        {canEdit && (
          <>
            <Row justify="center">
              <PrimaryButton title="Invite to group" icon={<AddIcon />} onClick={onClickInviteToGroup} />
            </Row>
            <HorizontalSpacer spacing={1} />
          </>
        )}
        <Content>
          <HorizontalSpacer spacing={3} />
          {myOrgName === member.member.suborg && (
            <>
              <H4>Organization</H4>
              <OrgRow justify="between" align="center">
                <NameRoleSection>
                  <H3>{myOrgName}</H3>
                </NameRoleSection>
                {canEdit && (
                  <PrimaryButton
                    title={`Remove from ${myOrgName}`}
                    icon={<PersonRemoveIcon />}
                    onClick={() => setremoveFromOrgModalOpen(true)}
                  />
                )}
              </OrgRow>
            </>
          )}
          {myOrgName !== member.member.suborg && (
            <div>
              <H4>Suborganization</H4>
              <OrgRow justify="between" align="center">
                <NameRoleSection>
                  <H3>{member.member.suborg}</H3>
                </NameRoleSection>
                {canEdit && (
                  <PrimaryButton
                    title={`Remove from ${member.member.suborg}`}
                    icon={<PersonRemoveIcon />}
                    onClick={() => setremoveFromOrgModalOpen(true)}
                  />
                )}
              </OrgRow>
            </div>
          )}
          <HorizontalSpacer spacing={3} />
          <H4>Groups</H4>
          {member.member.groups?.map((group) => (
            <MemberActionBar
              key={group.id}
              group={group}
              member={member}
              role={findRoleInGroup(group, member)}
              canEdit={canEdit}
              onClickRemoveFromGroup={onClickRemoveFromGroup}
              loading={loading}
            />
          ))}
        </Content>
      </SlideOutHeight>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {removeFromOrgModalOpen && (
          <Modal
            open={removeFromOrgModalOpen}
            onClose={() => setremoveFromOrgModalOpen(!removeFromOrgModalOpen)}
            closeIcon
          >
            <RemoveMemberFromOrgOrGroupModalContent
              memberName={member.member.name ?? ""}
              onClose={() => setremoveFromOrgModalOpen(!removeFromOrgModalOpen)}
              onClickDelete={onClickRemoveFromOrganization}
            />
          </Modal>
        )}
      </AnimatePresence>
    </ContentContainer>
  )
}

export default EditMemberSectionDropdown
